import { Edit2, Trash2 } from "lucide-react";
import { PaymentMethod } from "./Earnings";
import { Dispatch, SetStateAction, useState } from "react";
import Modal from "@/components/ui/Modal";
import { CustomButton } from "@/components/ui/Buttons";
import DeleteIcon from "@/assets/icons/settingsModal/DeleteIconBg.svg";

interface PaymentMethodCardProps {
  method: PaymentMethod;
  onEditAccount: (method: PaymentMethod) => void;
  onDeleteAccount: (id: string) => void;
  isDeleting: boolean;
  maskAccountNumber: (accountNumber: string) => string;
}

const PaymentMethodCard: React.FC<PaymentMethodCardProps> = ({
  method,
  onEditAccount,
  onDeleteAccount,
  isDeleting,
  maskAccountNumber,
}) => {
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-sm transition-shadow">
      <div className="flex justify-between items-start">
        <div className="space-y-1">
          <div className="flex items-center">
            <h3 className="font-medium text-gray-800">{method.label}</h3>
            {method.isDefault && (
              <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">
                Default
              </span>
            )}
          </div>
          <p className="text-gray-600">{method.details.bankName}</p>
          <p className="text-gray-500 text-sm">
            {maskAccountNumber(method.details.accountNumber)}
          </p>
        </div>

        <div className="flex space-x-1">
          <button
            onClick={() => onEditAccount(method)}
            className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
            aria-label="Edit"
          >
            <Edit2 size={18} />
          </button>
          <button
            onClick={() => setIsDeleteModalOpen(true)}
            className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
            disabled={isDeleting}
            aria-label="Delete"
          >
            <Trash2 size={18} />
          </button>
        </div>
      </div>
      <Modal
        isOpen={isDeleteModalOpen}
        setOpen={setIsDeleteModalOpen}
        width="400px"
      >
        <DeleteConfirmationModal
          onClose={setIsDeleteModalOpen}
          id={method._id}
          onDeleteAccount={onDeleteAccount}
        />
      </Modal>
    </div>
  );
};

export default PaymentMethodCard;

interface DeleteConfirmationModalProps {
  onClose: Dispatch<SetStateAction<boolean>>;
  id: string;
  onDeleteAccount: (id: string) => void;
}

const DeleteConfirmationModal = ({
  onClose,
  id,
  onDeleteAccount,
}: DeleteConfirmationModalProps) => {
  return (
    <div className="flex flex-col justify-center items-center">
      <DeleteIcon />
      <div className="space-y-2 mb-8 text-center">
        <h6 className="text-[#101828] text-lg leading-5 font-semibold mt-7">
          Delete Account
        </h6>
        <p className="text-[#475467] text-center text-sm leading-5">
          Are you sure you want to delete the account?
        </p>
      </div>
      <div className="w-full flex gap-3">
        <CustomButton
          onClick={() => onClose(false)}
          variant="outline"
          fontWeight="600"
          padding="8px"
          fontSize="16px"
          color="#344054"
          border="1px solid #D0D5DD"
          backgroundColor="#FFFFFF"
          className="w-full leading-6"
        >
          Cancel
        </CustomButton>
        <CustomButton
          onClick={() => onDeleteAccount(id)}
          fontWeight="600"
          padding="8px"
          fontSize="16px"
          color="#FFFFFF"
          backgroundColor="#17457D"
          className="w-full"
        >
          Confirm
        </CustomButton>
      </div>
    </div>
  );
};
